import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { api, formatApiError, useAuth } from "@/auth";
import { GROUP_OPTIONS, scrollToId } from "@/i18n";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400";

export default function AuthPage() {
  const { user, setUser, checking } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", group_code: "", assigned_groups: [] });
  const [busy, setBusy] = useState(false);

  if (!checking && user) return <Navigate to={user.role === "admin" ? "/gestion" : "/alumno"} replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const TEACHER_GROUP_CODES = GROUP_OPTIONS.map((o) => o.split(" · ")[0]);

  const toggleTeacherGroup = (code) =>
    setForm((f) => ({
      ...f,
      assigned_groups: (f.assigned_groups || []).includes(code)
        ? f.assigned_groups.filter((x) => x !== code)
        : [...(f.assigned_groups || []), code],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.info("Si ese email está registrado, te enviamos un enlace para restablecer la contraseña.");
        setMode("login");
        return;
      }
      const { data } =
        mode === "register"
          ? await api.post("/auth/register", form)
          : await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      toast.success(mode === "register" ? "¡Cuenta creada! Bienvenido a K-house." : `¡Bienvenida de nuevo, ${data.name}!`);
      navigate(data.role === "admin" ? "/gestion" : data.role === "teacher" ? "/profesora" : "/alumno");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 pb-16 pt-28">
      <p className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[26vw] font-black uppercase leading-none text-white/[0.03]">
        K-HOUSE
      </p>
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">
            {mode === "login" ? "Acceso alumnos" : mode === "teacher" ? "Acceso profesoras" : mode === "forgot" ? "Restablecer contraseña" : "Crear cuenta"}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {mode === "login"
              ? "Entra para ver eventos, horarios y avisos de tu grupo."
              : mode === "teacher"
                ? "Entra con tu cuenta del equipo de K-house para pasar lista y gestionar tus clases."
                : mode === "forgot"
                  ? "Escribe tu email y te enviamos un enlace para crear una nueva contraseña."
                  : form.role === "teacher"
                  ? "Crea tu cuenta del equipo de profesoras e indica tus clases."
                  : "Crea tu perfil de alumno/a de K-house."}
          </p>
        </div>

        {mode !== "forgot" && (
        <div className="mb-6 flex rounded-full border border-white/10 bg-panel p-1">
          {[
            { id: "login", label: "Acceder", test: "auth-tab-login" },
            { id: "teacher", label: "Profesoras", test: "auth-tab-teacher" },
            { id: "register", label: "Crear cuenta", test: "auth-tab-register" },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`auth-tab-${tab.id}`}
              onClick={() => setMode(tab.id)}
              className={`flex-1 rounded-full py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
                mode === tab.id ? "bg-accent text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        )}

        <form
          data-testid="auth-form"
          onSubmit={submit}
          className="space-y-5 rounded-2xl border border-white/10 bg-panel p-6 shadow-[0_0_40px_rgba(255,0,122,0.12)] sm:p-8"
        >
          {mode === "register" && (
            <div>
              <p className={labelCls}>¿Quién se registra? *</p>
              <div className="flex gap-2">
                {[
                  { id: "student", label: "Soy alumno/a", test: "auth-reg-role-student" },
                  { id: "teacher", label: "Soy profesora", test: "auth-reg-role-teacher" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={r.test}
                    onClick={() => setForm((f) => ({ ...f, role: r.id }))}
                    className={`flex-1 rounded-full border px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] transition-all duration-300 ${
                      form.role === r.id ? "border-accent bg-accent text-white" : "border-white/15 text-zinc-400"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {mode === "register" && (
            <div>
              <label htmlFor="auth-name" className={labelCls}>
                {form.role === "teacher" ? "Nombre de la profesora" : "Nombre del alumno/a"} *
              </label>
              <input id="auth-name" data-testid="auth-input-name" required value={form.name} onChange={set("name")} className={inputCls} />
            </div>
          )}
          <div>
            <label htmlFor="auth-email" className={labelCls}>Correo electrónico *</label>
            <input id="auth-email" data-testid="auth-input-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          {mode !== "forgot" && (
            <div>
              <label htmlFor="auth-password" className={labelCls}>Contraseña *</label>
              <input id="auth-password" data-testid="auth-input-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className={inputCls} />
            </div>
          )}
          {mode === "register" && form.role === "student" && (
            <div>
              <label htmlFor="auth-group" className={labelCls}>Tu grupo *</label>
              <select id="auth-group" data-testid="auth-select-group" required value={form.group_code} onChange={set("group_code")} className={`${inputCls} appearance-none`}>
                <option value="" disabled>Selecciona tu grupo</option>
                {GROUP_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-panel">{o}</option>
                ))}
              </select>
            </div>
          )}
          {mode === "register" && form.role === "teacher" && (
            <div>
              <p className={labelCls}>Tus clases *</p>
              <div className="flex flex-wrap gap-2">
                {TEACHER_GROUP_CODES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    data-testid={`auth-teacher-group-${code.replace(".", "-").toLowerCase()}`}
                    onClick={() => toggleTeacherGroup(code)}
                    className={`rounded-full border px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                      (form.assigned_groups || []).includes(code) ? "border-accent bg-accent text-white" : "border-white/15 text-zinc-400"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            type="submit"
            data-testid="auth-submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : mode === "login" ? <LogIn size={18} /> : <UserPlus size={18} />}
            {mode === "forgot" ? "Enviar enlace" : mode === "register" ? "Crear cuenta" : mode === "teacher" ? "Entrar como profesora" : "Entrar"}
          </button>
          {(mode === "login" || mode === "teacher") && (
            <button
              type="button"
              data-testid="auth-forgot-link"
              onClick={() => setMode("forgot")}
              className="mx-auto block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-accent"
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}
          {mode === "forgot" && (
            <button
              type="button"
              data-testid="auth-back-login"
              onClick={() => setMode("login")}
              className="mx-auto block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-accent"
            >
              Volver a acceder
            </button>
          )}
          {mode === "register" && form.role === "student" && (
            <p data-testid="auth-registration-note" className="text-center text-xs leading-relaxed text-zinc-400">
              Solo pueden crearse perfiles de alumnado matriculado: envía el formulario y, cuando la academia valide tu matrícula, regístrate con el mismo email.{" "}
              <button
                type="button"
                data-testid="auth-goto-inquiry"
                onClick={() => {
                  navigate("/");
                  setTimeout(() => scrollToId("contacto"), 150);
                }}
                className="font-bold text-accent hover:underline"
              >
                Rellenar formulario de inscripción
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
